import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Clock, MapPin, Calendar, UserPlus, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OpenPlayPage = () => {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skillFilter, setSkillFilter] = useState('all');
  const [joiningSession, setJoiningSession] = useState(null);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const params = skillFilter !== 'all' ? `?skill_level=${skillFilter}` : '';
        const response = await fetch(`${API}/open-play${params}`);
        if (response.ok) {
          setSessions(await response.json());
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [skillFilter]);

  const handleJoin = async (sessionId) => {
    setJoiningSession(sessionId);
    try {
      const response = await fetch(`${API}/open-play/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh sessions
        const sessionsRes = await fetch(`${API}/open-play`);
        if (sessionsRes.ok) {
          setSessions(await sessionsRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join session');
      }
    } catch (error) {
      toast.error('Failed to join session');
    } finally {
      setJoiningSession(null);
    }
  };

  const handleLeave = async (sessionId) => {
    setJoiningSession(sessionId);
    try {
      const response = await fetch(`${API}/open-play/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (response.ok) {
        toast.success('Left session');
        const sessionsRes = await fetch(`${API}/open-play`);
        if (sessionsRes.ok) {
          setSessions(await sessionsRes.json());
        }
      }
    } catch (error) {
      toast.error('Failed to leave session');
    } finally {
      setJoiningSession(null);
    }
  };

  const getSkillBadgeClass = (skill) => {
    switch (skill?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-blue-100 text-blue-800';
      case 'advanced': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Group sessions by date
  const groupedSessions = sessions.reduce((acc, session) => {
    const date = session.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="open-play-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Open Play Sessions</h1>
          <p className="text-muted-foreground">Join other players for casual games</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={skillFilter} onValueChange={setSkillFilter}>
            <SelectTrigger className="w-[180px]" data-testid="skill-filter">
              <SelectValue placeholder="All Skill Levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Skill Levels</SelectItem>
              <SelectItem value="Beginner">Beginner</SelectItem>
              <SelectItem value="Intermediate">Intermediate</SelectItem>
              <SelectItem value="Advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sessions by Date */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-xl"></div>)}
          </div>
        ) : Object.keys(groupedSessions).length > 0 ? (
          <div className="space-y-8">
            {Object.entries(groupedSessions).map(([date, dateSessions]) => (
              <div key={date}>
                <h2 className="font-heading font-semibold text-lg mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-primary" />
                  {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h2>
                <div className="grid gap-4">
                  {dateSessions.map((session) => (
                    <Card key={session.session_id} className="card-hover" data-testid={`session-${session.session_id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getSkillBadgeClass(session.skill_level)}>
                                {session.skill_level}
                              </Badge>
                              <Badge variant="outline">{session.venue_name}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground flex items-center mb-1">
                              <Clock className="h-4 w-4 mr-1" />
                              {session.start_time} - {session.end_time}
                            </p>
                            <p className="font-medium mt-2">
                              ₱{session.price_per_player} per player
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <div className="flex items-center justify-end mb-2">
                              <Users className="h-4 w-4 mr-1 text-muted-foreground" />
                              <span className={`font-medium ${session.current_players >= session.max_players ? 'text-destructive' : ''}`}>
                                {session.current_players}/{session.max_players}
                              </span>
                            </div>
                            
                            {session.current_players >= session.max_players ? (
                              <Badge variant="destructive">Full</Badge>
                            ) : (
                              <Button
                                onClick={() => handleJoin(session.session_id)}
                                disabled={joiningSession === session.session_id}
                                className="btn-primary"
                                data-testid={`join-${session.session_id}`}
                              >
                                {joiningSession === session.session_id ? 'Joining...' : (
                                  <>
                                    <UserPlus className="h-4 w-4 mr-1" />
                                    Join
                                  </>
                                )}
                              </Button>
                            )}
                            
                            {session.waitlist?.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {session.waitlist.length} on waitlist
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2">No sessions available</h3>
            <p className="text-muted-foreground">Check back later for open play sessions</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OpenPlayPage;
